import { useState } from "react";

import { AjaxManager } from "@impero-com/common/utils/ajax";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { TimezoneKey } from "@impero-com/common/utils/timezone";
import {
    FormLabel,
    renderMarkdown,
    Section,
    SectionContent,
    SectionGroup,
    SectionHeader,
} from "@impero-com/ui";
import { Select } from "antd";
import i18next from "i18next";
import { Link } from "react-router-dom";

import type { UserProfile } from "api/generated/model";
import { Locale } from "api/generated/model";
import { ApiKeys } from "components/apiKeys";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { FormLink } from "components/form/legacyFields/formLink";
import { SelectInput } from "components/form/legacyFields/selectInput";
import { StringInput } from "components/form/legacyFields/stringInput";
import { OAuthConnectionsSection } from "components/oauthConnections/OAuthConnectionsSection";
import { PersonalAccessTokens } from "components/personalAccessTokens";
import { TimezoneTypeahead } from "components/timezoneTypeahead/timezoneTypeahead";
import { getCultureInformationByName } from "utils/locale";

import { WebRoute } from "../../routes";
import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { useUserWithDetailsQuery } from "../user/queries/useUserWithDetailsQuery";

import { ChangePassword } from "./changePassword";
import { ForgotRecoveryCode } from "./forgotRecoveryCode";
import { NavigationSettings } from "./NavigationSettings";
import { NotificationFrequencySettings } from "./NotificationFrequencySettings";
import { NotificationPreferences } from "./NotificationPreferences";
import * as theme from "./ProfileSettingsPage.css";
import { useCulturesQuery } from "./queries/useCulturesQuery";
import { useUpdateUserMutation } from "./queries/useUpdateUserMutation";
import { useUpdateUserSettingsMutation } from "./queries/useUpdateUserSettingsMutation";
import { useUserSettingsQuery } from "./queries/useUserSettingsQuery";

interface ProfileSettingsPageProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
    orgCookie: OrgCookie;
}

export function ProfileSettingsPage({
    ajaxManager,
    orgProfileCookie,
    orgCookie,
}: ProfileSettingsPageProps) {
    if (!orgProfileCookie) {
        console.error("Failed to retrieve profile");
        return null;
    }

    return (
        <ProfileSettings
            ajaxManager={ajaxManager}
            orgProfileCookie={orgProfileCookie}
            orgCookie={orgCookie}
        />
    );
}

interface ProfileSettingsProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile;
    orgCookie: OrgCookie;
}

function ProfileSettings({
    ajaxManager,
    orgProfileCookie,
    orgCookie,
}: ProfileSettingsProps) {
    const { t } = useTranslation();
    const userId = orgProfileCookie.userId;

    const { data: userWithDetails } = useUserWithDetailsQuery(userId);
    const { data: cultures } = useCulturesQuery();
    const { data: userSettings } = useUserSettingsQuery(userId);

    const updateUser = useUpdateUserMutation();
    const updateUserSettings = useUpdateUserSettingsMutation();

    const notificationSettingsEnabled = useFeatureFlag(
        "userNotificationSettings"
    );
    const notificationFrequencyEnabled = useFeatureFlag(
        "userNotificationFrequency"
    );
    const personalAccessTokensEnabled = useFeatureFlag("personalAccessTokens");
    const newNavigationEnabled = useFeatureFlag("newNavigation");

    const [popup, setPopup] = useState<ProfilePopup>(null);

    const user = userWithDetails.content.resource;
    const canCreateApiKey = userWithDetails.content.actions.createApiKey;

    const cultureOptions = cultures.map((culture) => {
        const cultureInfo = getCultureInformationByName(culture.name);
        return cultureInfo
            ? { label: cultureInfo.label, value: cultureInfo.name }
            : { label: culture.name, value: culture.name };
    });

    // `culture` is safe to cast because cultureOptions values are always locales
    const handleCultureChange = (culture: string | undefined) =>
        updateUser
            .mutateAsync({
                userId,
                data: { user: { culture: culture as Locale } },
            })
            .then(() => {
                i18next.changeLanguage(culture || "en");
            });

    return (
        <>
            <ProfileDialogs
                popup={popup}
                onClose={() => setPopup(null)}
                ajaxManager={ajaxManager}
                orgCookie={orgCookie}
                orgProfileCookie={orgProfileCookie}
            />
            <SectionGroup>
                <Section>
                    <SectionHeader
                        level={4}
                        titleLabel={t("MyProfile_UserDetails_Title")}
                    />
                    <SectionContent>
                        <StringInput
                            id="firstname"
                            label={t("MyProfile_UserDetails_FirstName")}
                            type="EDIT"
                            value={user.firstname}
                            onChange={(firstname) =>
                                updateUser.mutateAsync({
                                    userId,
                                    data: { user: { firstname } },
                                })
                            }
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                        />
                        <StringInput
                            id="lastname"
                            label={t("MyProfile_UserDetails_LastName")}
                            type="EDIT"
                            value={user.lastname}
                            onChange={(lastname) =>
                                updateUser.mutateAsync({
                                    userId,
                                    data: { user: { lastname } },
                                })
                            }
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                        />
                        <StringInput
                            label={t("MyProfile_UserDetails_Email")}
                            type="READ_ONLY"
                            value={user.email}
                        />
                        <FormLink
                            label={t("MyProfile_Password_Title")}
                            linkName={t("MyProfile_Password_ChangePassword")}
                            action={{
                                type: "CLICK",
                                onClick: () => setPopup("CHANGE_PASSWORD"),
                            }}
                        />
                        {userSettings.twoFactorAuthEnabled && (
                            <BaseFormDisplay
                                label={t("MyProfile_TwoFactor_Title")}
                            >
                                <div className={theme.twoFactorLinks}>
                                    <Link to={WebRoute.SETUP_2FA}>
                                        {t("MyProfile_TwoFactor_TwoFactorLink")}
                                    </Link>
                                    <a
                                        onClick={() =>
                                            setPopup("FORGOT_RECOVERY_CODE")
                                        }
                                    >
                                        {t(
                                            "MyProfile_TwoFactor_ForgotRecoveryCode"
                                        )}
                                    </a>
                                </div>
                            </BaseFormDisplay>
                        )}
                    </SectionContent>
                </Section>

                <Section>
                    <SectionHeader
                        level={4}
                        titleLabel={t("MyProfile_LanguageAndTimezone_Title")}
                    />
                    <SectionContent>
                        <SelectInput
                            id="culture"
                            label={t("RegisterUser_UpdateUserDetails_Language")}
                            type="EDIT"
                            value={user.culture}
                            options={cultureOptions}
                            onChange={handleCultureChange}
                        />
                        <FormLabel>{t("Timezone")}</FormLabel>
                        <TimezoneTypeahead
                            id="timezone"
                            value={user.timezoneName as TimezoneKey}
                            onSelect={(timezoneName) =>
                                updateUser.mutate({
                                    userId,
                                    data: { user: { timezoneName } },
                                })
                            }
                        />
                    </SectionContent>
                </Section>

                {notificationSettingsEnabled && (
                    <NotificationPreferences userId={userId} />
                )}

                {newNavigationEnabled && <NavigationSettings />}

                {notificationFrequencyEnabled ? (
                    <NotificationFrequencySettings
                        userId={userId}
                        profilePage
                    />
                ) : (
                    <Section>
                        <SectionHeader
                            level={4}
                            titleLabel={t("MyProfile_EmailGrouping_Title")}
                            subtitleLabel={t(
                                "MyProfile_EmailGrouping_Description"
                            )}
                            showBorder
                        />
                        <SectionContent>
                            <Select
                                id="batch-notifications"
                                className={theme.emailGroupingSelect}
                                value={
                                    userSettings.batchNotifications
                                        ? "one_per_batch"
                                        : "each_control"
                                }
                                onChange={(value) =>
                                    updateUserSettings.mutate({
                                        userId,
                                        data: {
                                            batchNotifications:
                                                value === "one_per_batch",
                                        },
                                    })
                                }
                                options={[
                                    {
                                        value: "one_per_batch",
                                        label: t(
                                            "UserManagement_NotificationSettings_OnePerBatch"
                                        ),
                                    },
                                    {
                                        value: "each_control",
                                        label: t(
                                            "UserManagement_NotificationSettings_EachControl"
                                        ),
                                    },
                                ]}
                            />
                        </SectionContent>
                    </Section>
                )}

                {canCreateApiKey && (
                    <Section>
                        <SectionHeader
                            level={4}
                            titleLabel={t("ApiKey_Title")}
                            subtitleLabel={renderMarkdown(t("ApiKey_InfoBox"))}
                        />
                        <SectionContent>
                            <ApiKeys
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                            />
                        </SectionContent>
                    </Section>
                )}

                {personalAccessTokensEnabled && (
                    <Section>
                        <SectionHeader
                            level={4}
                            titleLabel={t("PersonalAccessToken_Title")}
                            subtitleLabel={renderMarkdown(
                                t("PersonalAccessToken_InfoBox")
                            )}
                        />
                        <SectionContent>
                            <PersonalAccessTokens
                                ajaxManager={ajaxManager}
                                orgProfileCookie={orgProfileCookie}
                            />
                        </SectionContent>
                    </Section>
                )}

                <OAuthConnectionsSection userId={userId} />
            </SectionGroup>
        </>
    );
}

type ProfilePopup = "CHANGE_PASSWORD" | "FORGOT_RECOVERY_CODE" | null;

interface ProfileDialogsProps {
    popup: ProfilePopup;
    onClose: () => void;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile;
}

function ProfileDialogs({
    popup,
    onClose,
    ajaxManager,
    orgCookie,
    orgProfileCookie,
}: ProfileDialogsProps) {
    switch (popup) {
        case null:
            return null;
        case "CHANGE_PASSWORD":
            return (
                <ChangePassword
                    ajaxManager={ajaxManager}
                    orgCookie={orgCookie}
                    user={orgProfileCookie}
                    onCloseDialog={onClose}
                />
            );
        case "FORGOT_RECOVERY_CODE":
            return <ForgotRecoveryCode onCloseDialog={onClose} />;
        default:
            return assertNever(popup);
    }
}
